import { FormEvent, Fragment, useState } from "react";
import { useApiClient } from "../api/useApiClient";
import type { CriteriosBusqueda, RespuestaBusqueda } from "../types/reviewItem";
import { useHasRole } from "../auth/useRoles";
import { ROLE_CONTADURIA } from "../constants/roles";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { HistorialRow, HistorialToggle } from "../components/HistorialRow";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { AvisoError } from "../components/AvisoError";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha } from "../utils/formato";

/** Mismo límite que el backend (BUSQUEDA_LARGO_MAXIMO). */
export const LARGO_MAXIMO_CRITERIO = 100;

/**
 * Valida antes de llamar al backend — que igual valida lo mismo y devuelve
 * 400. Devuelve el mensaje de error, o null si se puede buscar.
 */
export function validarCriterios(criterios: CriteriosBusqueda): string | null {
  const valores = Object.values(criterios).map((v) => (v ?? "").trim());
  if (!valores.some(Boolean)) return "Completá al menos un criterio: proveedor, número o monto.";
  if (valores.some((v) => v.length > LARGO_MAXIMO_CRITERIO)) {
    return `Cada criterio admite hasta ${LARGO_MAXIMO_CRITERIO} caracteres.`;
  }
  return null;
}

/**
 * Buscador — [NUEVO 20/10/2026, S-2] "¿dónde está esta factura?".
 *
 * Detrás del flag FUNCIONALIDAD_BUSCADOR del backend: el link del menú solo
 * aparece si /funcionalidades dice que está prendido. Contaduría busca en
 * todas las bandejas; un Aprobador solo en lo que ya tiene asignado para
 * aprobar — lo acota el backend, no esta pantalla.
 */
export function BuscarPage() {
  const api = useApiClient();
  const esContaduria = useHasRole(ROLE_CONTADURIA);
  const [criterios, setCriterios] = useState<CriteriosBusqueda>({ proveedor: "", numero: "", monto: "" });
  const [respuesta, setRespuesta] = useState<RespuestaBusqueda | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lineas, setLineas] = useState<Set<string>>(new Set());
  const [historial, setHistorial] = useState<Set<string>>(new Set());
  const columnas = 6;

  async function buscar(e: FormEvent) {
    e.preventDefault();
    const invalido = validarCriterios(criterios);
    // Ante un error no quedan a la vista los resultados de la búsqueda
    // anterior: parecerían la respuesta a lo que se acaba de pedir.
    if (invalido) {
      setError(invalido);
      setRespuesta(null);
      return;
    }
    setBuscando(true);
    setError(null);
    setLineas(new Set());
    setHistorial(new Set());
    try {
      setRespuesta(await api.buscarReviewItems(criterios));
    } catch (err) {
      setRespuesta(null);
      setError(err instanceof Error ? err.message : "Error desconocido al buscar.");
    } finally {
      setBuscando(false);
    }
  }

  function alternar(setter: typeof setLineas, id: string) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf") {
    try {
      abrirEnNuevaPestana(await api.obtenerBlobSasUrl(id, tipo));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  const campo = (clave: keyof CriteriosBusqueda) => ({
    value: criterios[clave] ?? "",
    maxLength: LARGO_MAXIMO_CRITERIO,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setCriterios((c) => ({ ...c, [clave]: e.target.value })),
  });

  return (
    <div>
      <h1>Buscar factura</h1>
      <p className="subtitle">
        {esContaduria
          ? "Busca en todas las bandejas y muestra en cuál está hoy cada factura."
          : "Busca entre las facturas que tenés asignadas para aprobar."}
      </p>

      <form className="buscador-form" onSubmit={buscar}>
        <label>
          Proveedor (nombre o RUT)
          <input type="text" {...campo("proveedor")} />
        </label>
        <label>
          Número (ej. A-1234)
          <input type="text" {...campo("numero")} />
        </label>
        <label>
          Monto exacto
          <input type="text" inputMode="decimal" placeholder="1.234,56" {...campo("monto")} />
        </label>
        <button type="submit" disabled={buscando}>
          {buscando ? "Buscando..." : "Buscar"}
        </button>
      </form>

      <AvisoError mensaje={error} onCerrar={() => setError(null)} />

      {respuesta && (
        <>
          {respuesta.hay_mas && (
            <p className="subtitle">
              Se muestran los primeros {respuesta.tope} resultados (los más recientes). Afiná la búsqueda para ver
              el resto.
            </p>
          )}
          <table className="compact">
            {/* Suma 100%: 15+22+11+9+18+25. */}
            <colgroup>
              <col style={{ width: "15%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "25%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Documento</th>
                <th>Proveedor</th>
                <th className="celda-monto">Monto</th>
                <th>Fecha doc.</th>
                <th>Bandeja</th>
                <th>Documentos</th>
              </tr>
            </thead>
            <tbody>
              {respuesta.resultados.length === 0 && (
                <tr>
                  <td colSpan={columnas} className="empty-row">
                    Ninguna factura coincide con la búsqueda.
                  </td>
                </tr>
              )}
              {respuesta.resultados.map((item) => (
                <Fragment key={item.id}>
                  <tr>
                    <td>
                      <CfeIdentidad
                        tipoCfe={item.tipo_cfe}
                        serie={item.serie}
                        nro={item.nro}
                        rutEmisor={item.rut_emisor}
                      />
                    </td>
                    <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                      {item.proveedor_nombre ?? "—"}
                    </td>
                    <td className="mono celda-monto">
                      {formatMonto(item.monto)}
                      {item.moneda && <span className="moneda">{item.moneda}</span>}
                    </td>
                    <td className="mono">{formatFecha(item.fecha_documento)}</td>
                    <td className="wrap-ok">
                      <b>{item.bandeja}</b>
                    </td>
                    <td className="actions-cell">
                      <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                        XML
                      </button>
                      {item.pdf_blob_url && (
                        <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                          PDF
                        </button>
                      )}
                      <LineasToggle expanded={lineas.has(item.id)} onToggle={() => alternar(setLineas, item.id)} />
                      {esContaduria && (
                        <HistorialToggle
                          expanded={historial.has(item.id)}
                          onToggle={() => alternar(setHistorial, item.id)}
                        />
                      )}
                    </td>
                  </tr>
                  {lineas.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={columnas} />}
                  {esContaduria && historial.has(item.id) && (
                    <HistorialRow reviewItemId={item.id} colSpan={columnas} />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
